import useApiRequest from "@/lib/hooks/use-request";
import { OrganizationListResponseDto } from "@/lib/interfaces/organisations";
import { OrganizationPreviewResponseDto } from "@/lib/interfaces/persons";
import { Paginated, Pagination } from "@/lib/interfaces/utils";
import LinkedItemManager from "@/ui-components/ui/linked-item-manager";
import { useCallback } from "react";
import OrganisationEditor from "./organisations/organisation-editor";

type OrganisationLinkedProps = {
  onAssign: (organizationId: string, title: string) => Promise<void>;
  pipelineId?: string;
} & (
  | {
      multiple: true;
      item: OrganizationPreviewResponseDto[];
      onRemove: (id: string) => Promise<void>;
    }
  | {
      multiple?: false;
      item?: OrganizationPreviewResponseDto;
      onRemove?: () => Promise<void>;
    }
);

const OrganisationLinked = ({ item, onAssign, onRemove, multiple = false, pipelineId }: OrganisationLinkedProps) => {
  const { apiRequest } = useApiRequest();

  const fetchSearchResults = useCallback(
    async (pagination: Pagination, searchText: string): Promise<Paginated<OrganizationListResponseDto>> => {
      try {
        let url = `contacts/organizations/search${pipelineId ? `/${pipelineId}` : ""}?limit=${
          pagination.pageSize
        }&page=${pagination.pageIndex + 1}`;
        if (searchText) {
          url = `${url}&search=${searchText}`;
        }
        const response = await apiRequest<Paginated<OrganizationListResponseDto>>(url, "GET");

        if (!response.data) {
          return {
            docs: [],
            hasNextPage: false,
            limit: pagination.pageSize,
            offset: pagination.pageIndex * pagination.pageSize,
            page: pagination.pageIndex,
            hasPrevPage: pagination.pageIndex > 0,
            count: 0,
            pageCount: 0,
          };
        }

        return response.data;
      } catch (error) {
        console.error("Error fetching organizations:", error);
        return {
          docs: [],
          hasNextPage: false,
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          page: pagination.pageIndex,
          hasPrevPage: pagination.pageIndex > 0,
          count: 0,
          pageCount: 0,
        };
      }
    },
    [apiRequest, pipelineId]
  );

  const renderSearchOption = useCallback(
    (organization: OrganizationListResponseDto) => ({
      label: organization.name,
      value: organization._id,
    }),
    []
  );

  if (multiple) {
    return (
      <>
        {Array.isArray(item) &&
          item.length > 0 &&
          item.map((organization) => (
            <div key={organization._id}>
              <LinkedItemManager<OrganizationListResponseDto>
                editor={<OrganisationEditor withoutRedirect={true} belongsToPipelineId={pipelineId} />}
                fetchSearchResults={fetchSearchResults}
                renderSearchOption={renderSearchOption}
                customOnSubmit={onAssign}
                removeItem={onRemove}
                link={{
                  title: `${organization.name} ${organization.address ? `(${organization.address})` : ""}`,
                  href: `/contacts/organisations/${organization._id}`,
                  id: organization._id,
                }}
                title="Unternehmen"
              />
            </div>
          ))}
        <div>
          <LinkedItemManager<OrganizationListResponseDto>
            editor={<OrganisationEditor withoutRedirect={true} belongsToPipelineId={pipelineId} />}
            fetchSearchResults={fetchSearchResults}
            renderSearchOption={renderSearchOption}
            customOnSubmit={onAssign}
            removeItem={onRemove}
            title="Unternehmen"
            multiple={true}
          />
        </div>
      </>
    );
  }

  return (
    <LinkedItemManager<OrganizationListResponseDto>
      editor={<OrganisationEditor withoutRedirect={true} belongsToPipelineId={pipelineId} />}
      fetchSearchResults={fetchSearchResults}
      renderSearchOption={renderSearchOption}
      customOnSubmit={onAssign}
      removeItem={onRemove}
      link={
        item && !Array.isArray(item)
          ? {
              title: `${item.name} ${item.address ? `(${item.address})` : ""}`,
              href: `/contacts/organisations/${item._id}`,
              id: item._id,
            }
          : undefined
      }
      title="Unternehmen"
    />
  );
};

export default OrganisationLinked;
